// PageControl — the PDF reader's compact "Sida x av y" control (L4): an
// editable page number (Enter/blur jumps, clamped; invalid input reverts),
// plus prev/next. Pure props in, one intent out (`onJump`). The only local
// state is the view-ephemeral draft text while the author is typing; keys are
// handled on the focused input only (no global chord listener).

import { Trans, useLingui } from "@lingui/react/macro";
import { useState } from "react";
import { Button } from "../Button/Button.js";
import { clampPage, parsePageInput } from "./pageNav.js";

export type PageControlProps = {
    readonly current: number;
    readonly pageCount: number;
    readonly onJump: (page: number) => void;
};

export const PageControl = ({
    current,
    pageCount,
    onJump,
}: PageControlProps) => {
    const { t } = useLingui();
    // null ⇒ not editing: the input mirrors the scroll-tracked page.
    const [draft, setDraft] = useState<string | null>(null);

    const commit = (): void => {
        const page = parsePageInput(draft ?? "", pageCount);
        setDraft(null);
        if (page !== null) onJump(page);
    };

    return (
        <div className="bilda-source-view__page-control">
            <Button
                variant="ghost"
                size="sm"
                disabled={current <= 1}
                aria-label={t`Previous page`}
                onClick={() => onJump(clampPage(current - 1, pageCount))}
            >
                ‹
            </Button>
            <span className="bilda-source-view__page-control-label">
                <Trans>Page</Trans>
                <input
                    className="bilda-source-view__page-input"
                    type="text"
                    inputMode="numeric"
                    aria-label={t`Go to page`}
                    size={String(pageCount).length + 1}
                    value={draft ?? String(current)}
                    onFocus={(e) => e.currentTarget.select()}
                    onChange={(e) => setDraft(e.currentTarget.value)}
                    onBlur={() => (draft === null ? undefined : commit())}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") commit();
                        if (e.key === "Escape") setDraft(null);
                    }}
                />
                <Trans>of {pageCount}</Trans>
            </span>
            <Button
                variant="ghost"
                size="sm"
                disabled={current >= pageCount}
                aria-label={t`Next page`}
                onClick={() => onJump(clampPage(current + 1, pageCount))}
            >
                ›
            </Button>
        </div>
    );
};
